import linkButtonStyles from '@github-ui/code-view-shared/css/LinkButton.module.css'
import {ownerPath, repositoryPath} from '@github-ui/paths'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize} from '@github-ui/screen-size'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {FileIcon} from '@primer/octicons-react'
import {Breadcrumbs, Link, LinkButton} from '@primer/react'
import type {PropsWithChildren} from 'react'

import styles from './OrgOnboardingTip.module.css'

interface OrgOnboardingTipProps {
  mediaUrl?: string
  mediaPreviewSrc?: string
  iconSvg: SafeHTMLString
  taskTitle: string
  taskPath: string
  org: string
}

export function OrgOnboardingTip({
  children,
  mediaUrl,
  mediaPreviewSrc,
  iconSvg,
  taskTitle,
  taskPath,
  org,
}: PropsWithChildren<OrgOnboardingTipProps>) {
  return (
    <section className={styles.Box}>
      <div className={styles.Box_1}>
        <SuggestIcon iconSvg={iconSvg} />

        <div className={styles.Box_2}>
          <Breadcrumbs>
            <Breadcrumbs.Item href={ownerPath({owner: org})}>Tasks</Breadcrumbs.Item>
            <Breadcrumbs.Item href={taskPath} className={styles.Breadcrumbs_Item}>
              {taskTitle}
            </Breadcrumbs.Item>
          </Breadcrumbs>
          {children}
        </div>
      </div>
      <Media mediaPreviewSrc={mediaPreviewSrc} mediaUrl={mediaUrl} />
    </section>
  )
}

function SuggestIcon({iconSvg}: {iconSvg: SafeHTMLString}) {
  return (
    <div className={styles.Box_3}>
      <VerifiedHTMLDiv html={iconSvg} className={styles.VerifiedHTMLBox} />
      <Bubble size={6} color="#6c84e9" bottom={-7} left={-7} />
      <Bubble size={4} color="#9e7bff" top={-4} right={4} />
      <Bubble size={6} color="#6c84e9" top={-7} right={-8} />
    </div>
  )
}

function Bubble({
  size,
  color,
  left,
  right,
  top,
  bottom,
}: {
  size: number
  color: string
  left?: number
  right?: number
  top?: number
  bottom?: number
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        left: `${left}px`,
        right: `${right}px`,
        top: `${top}px`,
        bottom: `${bottom}px`,
        backgroundColor: color,
      }}
      className={styles.Box_4}
    />
  )
}

function Media({mediaUrl, mediaPreviewSrc}: Pick<OrgOnboardingTipProps, 'mediaUrl' | 'mediaPreviewSrc'>) {
  //on the server assume we are on mobile where an image rendering then disappearing would be far more annoying
  //than an image popping in on a desktop browser
  if ((ssrSafeWindow?.innerWidth ?? 0) < ScreenSize.xlarge || !mediaUrl || !mediaPreviewSrc) return null

  return (
    <div className={styles.Box_5}>
      <Link href={mediaUrl} className={styles.Link}>
        <img src={mediaPreviewSrc} alt="Guidance" loading="lazy" style={{width: '50%', height: '50%'}} />
      </Link>
    </div>
  )
}

interface ActionsOnboardingPromptProps {
  repo: string
  owner: string
}
export function ActionsOnboardingPrompt({repo, owner}: ActionsOnboardingPromptProps) {
  return (
    <div>
      <h3 className={styles.Text}>Auto-assign new issue with GitHub Actions</h3>
      <div className={styles.Box_6}>
        <p>
          The <span className={styles.Text_1}>auto-assign.yml</span> file below lives inside your{' '}
          <span className={styles.Text_1}>demo-repository</span> and defines when and how it’s automatically triggered.
          This{' '}
          <a href="https://github.com/marketplace/actions/auto-assign-issues-prs" target="_blank" rel="noreferrer">
            “Auto Assign” workflow
          </a>{' '}
          happens to add reviewers and assignees to issues and pull requests when they’re opened in this repository.
        </p>

        <p>
          You can see the results of this workflow in any{' '}
          <a href={repositoryPath({owner, repo, action: 'issues'})}>issue</a> or{' '}
          <a href={repositoryPath({owner, repo, action: 'pulls'})}>pull request</a> that you create in this repository,
          as it’ll assign them to the specified members. And you can see a log of any workflows you run in{' '}
          <a href={repositoryPath({owner, repo, action: 'actions'})}>your repository’s “Actions” tab.</a>
        </p>

        <div className={styles.Box_7}>
          <LinkButton
            variant="primary"
            href={repositoryPath({owner, repo, action: 'issues/new'})}
            className={linkButtonStyles.linkButton}
          >
            Create new issue to see results
          </LinkButton>

          <Link href="https://docs.github.com/actions" target="_blank" className={styles.Link_1}>
            <FileIcon className={styles.Octicon} />
            Learn how automation works on GitHub
          </Link>
        </div>
      </div>
    </div>
  )
}
